import BaseDatafeed from './BaseDatafeed';
import io from 'socket.io-client';
import Logger from '../../../utils/Logger';

const CONFIG = {
  API_TIMEOUT: 30000,
  SOCKET_RECONNECTION_DELAY: 1000,
  SOCKET_RECONNECTION_MAX_DELAY: 5000,
  SOCKET_MAX_RECONNECTION_ATTEMPTS: 10,
};

/**
 * SpotDatafeed - Unocoin Spot Market Implementation
 * 
 * Handles Spot trading with Unocoin API using:
 * - REST API: GET /exchange/trading-view/1/history
 * - Socket.io for real-time updates
 * - Symbol format: INR-BTC (API), TradeHistory/BTC-INR (Socket)
 */
class SpotDatafeed extends BaseDatafeed {
  constructor(config) {
    super({
      ...config,
      exchange: 'Unocoin',
      timezone: config.timezone || 'Asia/Kolkata',
    });
    
    // Throw error if environment variables are missing
    if (!process.env.REACT_APP_API_URL || !process.env.REACT_APP_SPOT_SOCKET_URL) {
      throw new Error('Missing required environment variables: REACT_APP_API_URL and/or REACT_APP_SPOT_SOCKET_URL');
    }
    
    this.apiUrl = process.env.REACT_APP_API_URL;
    this.socketUrl = process.env.REACT_APP_SPOT_SOCKET_URL;
    this.supportedResolutions = ['5', '15', '30', '120', '240', 'D', '1D', 'W', '1W', 'M', '1M'];
    
    // Store event handlers for proper cleanup
    this.socketHandlers = {
      connect: null,
      connect_error: null,
      disconnect: null,
      error: null
    };
  }

  /**
   * Initialize Socket.io connection for Spot market
   */
  initializeSocket() {
    if (this.socket) return;

    Logger.info('[Spot] Initializing Socket.io connection to:', this.socketUrl);
    
    this.socket = io(this.socketUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: CONFIG.SOCKET_RECONNECTION_DELAY,
      reconnectionDelayMax: CONFIG.SOCKET_RECONNECTION_MAX_DELAY,
      reconnectionAttempts: CONFIG.SOCKET_MAX_RECONNECTION_ATTEMPTS
    });

    // Store handlers as named functions for cleanup
    this.socketHandlers.connect = () => {
      Logger.info('[Spot] Socket connected successfully! ID:', this.socket.id);
      
      // Re-subscribe to all active channels
      Object.values(this.subscribers).forEach(sub => {
        this.subscribeToSocket(sub.channelString);
      });
    };

    this.socketHandlers.connect_error = (error) => {
      Logger.error('[Spot] Socket connection error:', error.message);
    };

    this.socketHandlers.disconnect = (reason) => {
      Logger.warn('[Spot] Socket disconnected. Reason:', reason);
    };

    this.socketHandlers.error = (error) => {
      Logger.error('[Spot] Socket error:', error);
    };
    
    // Register all event handlers
    this.socket.on('connect', this.socketHandlers.connect);
    this.socket.on('connect_error', this.socketHandlers.connect_error);
    this.socket.on('disconnect', this.socketHandlers.disconnect);
    this.socket.on('error', this.socketHandlers.error);

    // Log all incoming events for debugging
    this.socket.onAny((eventName, ...args) => {
      Logger.debug('[Spot] Socket event:', eventName, args);
      
      // Handle real-time updates
      if (args[0] && args[0].data) {
        this.handleSocketMessage(args[0].data, eventName);
      }
    });
  }

  /**
   * Format symbol for Unocoin API: "INR-BTC"
   */
  formatSymbolForAPI(baseCoin, cryptoCoin) {
    return `${baseCoin}-${cryptoCoin}`;
  }

  /**
   * Format channel for Socket.io: "TradeHistory/BTC-INR"
   */
  formatSymbolForSocket(baseCoin, cryptoCoin, resolution) {
    return `TradeHistory/${cryptoCoin}-${baseCoin}`;
  }

  /**
   * Map TradingView resolution to Unocoin API resolution
   */
  mapResolution(resolution) {
    const resolutionMap = {
      '5': 5,
      '15': 15,
      '30': 30,
      '60': 60,
      '120': 120,
      '240': 240,
      'D': '1D',
      '1D': '1D',
      'W': '1W',
      '1W': '1W',
      'M': '1M',
      '1M': '1M',
    };
    return resolutionMap[resolution] || 60;
  }

  /**
   * Fetch historical bars from Unocoin API
   */
  async fetchHistoricalBars(symbolInfo, resolution, periodParams) {
    const { from, to } = periodParams;
    const [baseCoin, cryptoCoin] = symbolInfo.ticker.split('/');
    const symbol = this.formatSymbolForAPI(baseCoin, cryptoCoin);
    const apiResolution = this.mapResolution(resolution);

    const url = `${this.apiUrl}exchange/trading-view/1/history?symbol=${symbol}&resolution=${apiResolution}&from=${from}&to=${to}`;
    
    Logger.info(`[Spot] Fetching bars: ${url}`);

    try {
      // Add timeout to prevent indefinite hangs
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.API_TIMEOUT);
      
      const response = await fetch(url, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.s !== 'ok' || !data.t || data.t.length === 0) {
        console.warn('⚠️ [Spot] No data available');
        return [];
      }

      // Transform API response to TradingView bars format
      const bars = data.t.map((time, index) => ({
        time: time * 1000, // Convert to milliseconds
        open: parseFloat(data.o[index]),
        high: parseFloat(data.h[index]),
        low: parseFloat(data.l[index]),
        close: parseFloat(data.c[index]),
        volume: parseFloat(data.v[index])
      }));

      Logger.info(`[Spot] Received ${bars.length} bars`);
      Logger.debug(`[Spot] First bar:`, bars[0]);
      Logger.debug(`[Spot] Last bar:`, bars[bars.length - 1]);
      return bars;

    } catch (error) {
      if (error.name === 'AbortError') {
        Logger.error('[Spot] API request timeout after', CONFIG.API_TIMEOUT / 1000, 'seconds');
      } else {
        Logger.error('[Spot] fetchHistoricalBars error:', error);
      }
      throw error;
    }
  }

  /**
   * Parse Socket.io message for real-time updates
   */
  parseSocketMessage(data, channelString = null) {
    try {
      if (!data || typeof data !== 'object') return null;

      // Extract symbol from channel name if available
      // Channel format: "TradeHistory/BTC-INR" -> "BTCINR"
      let symbol = null;
      if (channelString) {
        const match = channelString.match(/TradeHistory\/([A-Z]+)-([A-Z]+)/i);
        if (match) {
          symbol = `${match[1]}${match[2]}`.toUpperCase();
        }
      }

      // Unocoin sends trade updates directly
      const bar = {
        time: data.timestamp ? data.timestamp * 1000 : Date.now(),
        open: parseFloat(data.open || data.price),
        high: parseFloat(data.high || data.price),
        low: parseFloat(data.low || data.price),
        close: parseFloat(data.close || data.price),
        volume: parseFloat(data.volume || 0),
        symbol: symbol // Add symbol for validation in handleSocketMessage
      };

      // Validate bar data
      if (isNaN(bar.time) || isNaN(bar.close)) {
        Logger.warn('[Spot] Invalid bar data:', data);
        return null;
      }

      return bar;

    } catch (error) {
      Logger.error('[Spot] parseSocketMessage error:', error);
      return null;
    }
  }

  /**
   * Subscribe to Socket.io channel
   */
  subscribeToSocket(channelString) {
    if (!this.socket || !this.socket.connected) {
      Logger.warn('[Spot] Socket not connected, deferring subscription to:', channelString);
      return;
    }

    Logger.info('[Spot] Subscribing to channel:', channelString);
    this.socket.emit('subscribe', { channel: channelString });
  }

  /**
   * Unsubscribe from Socket.io channel
   */
  unsubscribeFromSocket(channelString) {
    if (!this.socket) return;

    Logger.info('[Spot] Unsubscribing from channel:', channelString);
    this.socket.emit('unsubscribe', { channel: channelString });
  }
  
  /**
   * Clean up resources and remove event listeners
   */
  destroy() {
    Logger.info('[Spot] Destroying datafeed and cleaning up resources');
    
    // Remove all event listeners to prevent memory leaks
    if (this.socket) {
      // Remove specific event handlers
      if (this.socketHandlers.connect) {
        this.socket.off('connect', this.socketHandlers.connect);
      }
      if (this.socketHandlers.connect_error) {
        this.socket.off('connect_error', this.socketHandlers.connect_error);
      }
      if (this.socketHandlers.disconnect) {
        this.socket.off('disconnect', this.socketHandlers.disconnect);
      }
      if (this.socketHandlers.error) {
        this.socket.off('error', this.socketHandlers.error);
      }
      
      // Remove the onAny listener
      this.socket.offAny();
      
      // Disconnect and cleanup
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Clear handler references
    this.socketHandlers = {};
    
    // Call parent cleanup if exists
    if (super.destroy) {
      super.destroy();
    }
  }
}

export default SpotDatafeed;
